import { visitJobId } from './jobs'
import { isUnplaced } from './planning'
import type { BookedCompanion, Clean } from './types'
import { addDaysISO, dayLabel, mondayOf, shortDate } from './ui'

// "Also booked Thu 1 Oct": a customer with more than one visit booked says so
// on each of them (Liam 29 Sep 2026: Gabriella was on Thu 1 Oct and in the
// week of 28 Sep, and nothing on either row said so). Booking twice on
// purpose stays allowed ("we do a customer but then we have to go back"), so
// this only tells; it never blocks and never removes anything. Owner screens
// only: the companions come from the diary's own read, which workers never get.

// Per JOB (several jobs, 0042): the server lists a customer's visits per job, and windows and gutters both booked
// are two jobs each booked once, never "also booked". A visit with no job is job 1's, as every visit is on a round
// with several jobs off.
// Two visits held in the same week with no day (Liam's phone, 9 Oct 2026: Gabriella's To place card read "week of
// 5 Oct, no day yet" and then "Also in the week of 5 Oct") say "Booked twice this week" instead of naming the week
// the card has just named.
export function alsoBookedText(
  cl: Pick<Clean, 'id' | 'customer_id' | 'state'> & Partial<Pick<Clean, 'date' | 'confirmed' | 'source' | 'created_by'>> & { job_id?: number | null },
  companions: readonly (BookedCompanion & { job_id?: number | null })[] | undefined,
  today: string,
): string {
  if (cl.state !== 'booked' || !companions?.length) return ''
  const heldWeek = (v: Pick<Clean, 'state' | 'confirmed' | 'source' | 'date' | 'created_by'>): string | null => {
    const monday = mondayOf(v.date)
    return isUnplaced(v, monday, addDaysISO(monday, 6)) ? monday : null
  }
  const ownWeek = cl.date !== undefined && cl.confirmed !== undefined && cl.source !== undefined
    ? heldWeek({ state: cl.state, confirmed: cl.confirmed, source: cl.source, date: cl.date, created_by: cl.created_by ?? null })
    : null
  let twice = false
  const parts = companions
    .filter((o) => o.customer_id === cl.customer_id && visitJobId(o) === visitJobId(cl) && o.id !== cl.id && o.state === 'booked')
    .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
    .flatMap((o) => {
      const week = heldWeek(o)
      if (week && week === ownWeek) { twice = true; return [] }
      if (week) return [`in the week of ${shortDate(week)}`]
      return [o.date < today ? `booked ${dayLabel(o.date)} (missed)` : `booked ${dayLabel(o.date)}`]
    })
  return [twice ? 'Booked twice this week' : '', parts.length ? `Also ${parts.join(' and ')}` : ''].filter(Boolean).join(' · ')
}
